import React, { useEffect, useState } from 'react';
import { I18n } from 'react-redux-i18n';

import { I18N_BASE, KEYBOARD_SHORTCUTS } from '../../constants';
import { ActiveDecisionSummary, RESET_REASON_MAX_LENGTH } from '../../models';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import CmButton from '../../components/primitives/CmButton';
import CmModal from '../../components/primitives/CmModal';
import NotesField from './NotesField';
import styles from './modals.module.css';

export interface ResetDecisionModalProps {
  open: boolean;
  decision: ActiveDecisionSummary | null;
  isSubmitting: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
  testId?: string;
}

export function ResetDecisionModal({
  open,
  decision,
  isSubmitting,
  onCancel,
  onConfirm,
  testId,
}: ResetDecisionModalProps): React.ReactElement {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!open) setReason('');
  }, [open]);

  const trimmed = reason.trim();
  const tooLong = reason.length > RESET_REASON_MAX_LENGTH;
  const submitDisabled = isSubmitting || !trimmed || tooLong;

  const handleConfirm = () => {
    if (submitDisabled) return;
    onConfirm(trimmed);
  };

  useKeyboardShortcuts(
    { [KEYBOARD_SHORTCUTS.confirm]: handleConfirm },
    { enabled: open },
  );

  return (
    <CmModal
      open={open}
      onClose={onCancel}
      title={I18n.t(`${I18N_BASE}.resetModal.title`)}
      testId={testId}
    >
      <p>
        {decision
          ? I18n.t(`${I18N_BASE}.resetModal.message`, {
              decision: decision.decision,
            })
          : I18n.t(`${I18N_BASE}.resetModal.messageEmpty`)}
      </p>
      {decision?.decision === 'block' ? (
        <p className={styles.resetCascadeNote} data-testid="reset-cascade-note">
          {I18n.t(`${I18N_BASE}.resetModal.blockReversalNote`)}
        </p>
      ) : null}
      <NotesField
        value={reason}
        onChange={setReason}
        maxLength={RESET_REASON_MAX_LENGTH}
        placeholderKey={`${I18N_BASE}.resetModal.reasonPlaceholder`}
        disabled={isSubmitting}
        testId="reset-reason"
      />
      <div className={styles.buttonRow}>
        <CmButton variant="ghost" onClick={onCancel} testId="reset-cancel">
          {I18n.t(`${I18N_BASE}.resetModal.cancel`)}
        </CmButton>
        <CmButton
          variant="danger"
          onClick={handleConfirm}
          disabled={submitDisabled}
          loading={isSubmitting}
          testId="reset-confirm"
        >
          {I18n.t(`${I18N_BASE}.resetModal.confirm`)}
        </CmButton>
      </div>
    </CmModal>
  );
}

export default ResetDecisionModal;
